import _ from 'lodash'
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  IRowNode,
  MenuItemDef,
} from 'ag-grid-community'
import {
  allDescendants,
  focusRow,
  getSelectedNode,
} from '../../../containers/BulkSheetView/lib/gridApi'
import {
  createNewProjectPlanNewRow,
  createSameNameTaskRow,
  ProjectPlanNewRow,
} from '../projectPlanNew'
import { intl } from '../../../../i18n'
import {
  addRowsBefore,
  addRowsToLastChild,
  updateRows,
} from '../../../containers/BulkSheetView/hooks/actions/crudTreeRows'
import { dateTermToViewConfig, openProgressReport } from '../../ProgressReport'
import store from '../../../../store'
import { open } from '../../../router'
import {
  openWbsItemSearch,
  SearchConditionEndDelayed,
} from '../../WbsItemSearch/wbsItemSearchOptions'
import { openTicketList } from '../../TicketLists/TicketListsOptions'
import { getParent } from '../../../containers/BulkSheetView/lib/tree'
import {
  ContextMenuItemId,
  getMenuIconHtml,
} from '../../../containers/commons/AgGrid/lib/contextMenu'
import { WbsItemStatus } from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import DateVO from '../../../../vo/DateVO'
import { canParseDate } from '../../../../utils/date'
import { getScheduledDateRanges } from '../../../../lib/functions/projectPlan'
import { ReportReCalculator } from './cumulation'
import { openKanban } from '../../Kanban/Kanban'
import { StatusKanbanSearchCondition } from '../../Kanban/hooks/useSearchCondition'
import { moveRowsToLastChild } from '../../../containers/BulkSheetView/hooks/actions/moveTreeRows'
import ReactDOMServer from 'react-dom/server'
import { TicketListSubMenuItemsIcon } from '../../../components/icons/TicketListSubMenuItemsIcon'
import { WbsItemCodeGenerator } from '../../../../applications/ports/wbsItemCodeService'

export const addRowAboveMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  setData: (d: ProjectPlanNewRow[], r: ProjectPlanNewRow[]) => void,
  generateCode: WbsItemCodeGenerator
): MenuItemDef[] => {
  const row = params.node?.data
  const parent = getParent(data, row)
  const count = params.api.getSelectedNodes().length

  const types = store.getState().project.wbsItemTypes
  const subMenuTypes = !parent?.body
    ? []
    : [
        types.process,
        types.deliverableList,
        types.deliverable,
        types.task,
      ].filter(type => type.canBeChildOf(parent.body!.wbsItem.wbsItemType))

  if (subMenuTypes.length === 0) return []

  const subMenu = subMenuTypes.map(type => ({
    name: type.name,
    icon: `<img src="${type.iconUrl}" />`,
    action: () => {
      const rows = Array.from({ length: count }).map(_ =>
        createNewProjectPlanNewRow(type, generateCode)
      )
      const newData = addRowsBefore(data, rows, row.uuid)
      const uuids = rows.map(v => v.uuid)
      const newRows = newData.filter(v => uuids.includes(v.uuid))
      setData(newData, newRows)
      rows[0]?.uuid && focusRow(params.api, rows[0].uuid)
    },
    shortcut: intl.formatMessage(
      { id: 'bulksheet.contextMenu.shortcut.alt.shift' },
      {
        shortcutKey: type.isProcess()
          ? 'H'
          : type.isDeliverableList()
            ? 'J'
            : type.isDeliverable()
              ? 'K'
              : 'L',
      }
    ),
  }))

  return [
    {
      name: intl.formatMessage({
        id: 'bulksheet.contextMenu.insert.above.row',
      }),
      icon: getMenuIconHtml(ContextMenuItemId.ADD_ROW),
      subMenu,
    },
  ]
}

export const addRowToChildMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  setData: (d: ProjectPlanNewRow[], r: ProjectPlanNewRow[]) => void,
  generateCode: WbsItemCodeGenerator
): MenuItemDef[] => {
  const row = params.node?.data

  const types = store.getState().project.wbsItemTypes
  const subMenuTypes = !row?.body
    ? []
    : [
        types.process,
        types.deliverableList,
        types.deliverable,
        types.task,
      ].filter(type => type.canBeChildOf(row.body!.wbsItem.wbsItemType))

  const subMenu = subMenuTypes.map(type => ({
    name: type.name,
    icon: `<img src="${type.iconUrl}" />`,
    action: () => {
      const newRow = createNewProjectPlanNewRow(type, generateCode)
      const newData = addRowsToLastChild(data, [newRow], row.uuid)
      const newRows = newData.filter(v => v.uuid === newRow.uuid)
      setData(newData, newRows)
      setTimeout(() => {
        params.node?.setExpanded(true)
        focusRow(params.api, newRow.uuid)
      }, 0)
    },
    shortcut: intl.formatMessage(
      { id: 'bulksheet.contextMenu.shortcut.ctrl.shift' },
      {
        shortcutKey: type.isProcess()
          ? 'H'
          : type.isDeliverableList()
            ? 'J'
            : type.isDeliverable()
              ? 'K'
              : 'L',
      }
    ),
  }))

  if (subMenu.length === 0) return []

  return [
    {
      name: intl.formatMessage({
        id: 'bulksheet.contextMenu.insert.toChild.row',
      }),
      icon: getMenuIconHtml(ContextMenuItemId.ADD_ROW),
      subMenu,
    },
  ]
}

export const addRowsToChildMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  action: {
    onAddRows: (type: WbsItemTypeVO, parentUuid: string) => void
  }
): MenuItemDef[] => {
  const row = params.node?.data

  const wbsItemTypes = store.getState().project.wbsItemTypes
  const subMenuTypes = wbsItemTypes
    .getAll()
    .filter(
      type =>
        !type.isWorkgroup() && type.canBeChildOf(row.body!.wbsItem.wbsItemType)
    )

  if (subMenuTypes.length === 0) return []

  return [
    {
      name: intl.formatMessage({ id: 'addMultipleRows.wbsItem' }),
      icon: getMenuIconHtml(ContextMenuItemId.ADD_MULTIPLE_ROW),
      subMenu: subMenuTypes.map(type => ({
        name: type.name,
        icon: `<img src="${type.iconUrl}" />`,
        action: () => action.onAddRows(type, row.uuid),
      })),
    },
  ]
}

export const addWorkgroupMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  action: { setData: (data: ProjectPlanNewRow[]) => void },
  generateCode: WbsItemCodeGenerator
) => {
  const wbsItemTypes = store.getState().project.wbsItemTypes
  const workgroupType = wbsItemTypes.workgroup
  return [
    {
      name: intl.formatMessage({ id: 'bulksheet.contextMenu.workgroup' }),
      icon: `<img src="${workgroupType.iconUrl}" />`,
      action: () => {
        const workgroupType = store.getState().project.wbsItemTypes.workgroup
        const workgroup = createNewProjectPlanNewRow(
          workgroupType,
          generateCode
        )
        workgroup.treeValue = [workgroup.uuid]
        workgroup.added = true
        action.setData([...data.concat(), workgroup])
        focusRow(params.api, workgroup.uuid)
      },
    },
  ]
}

export const changeProcessToWorkgroupMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  action: {
    setData: (data: ProjectPlanNewRow[], moved: ProjectPlanNewRow[]) => void
  }
) => {
  const row: ProjectPlanNewRow = params.node?.data
  const isChangeable = row?.body?.wbsItem.wbsItemType.isProcess()

  const wbsItemTypes = store.getState().project.wbsItemTypes
  const workgroupType = wbsItemTypes.workgroup
  return [
    {
      name: intl.formatMessage({
        id: 'bulksheet.contextMenu.changeProcessToWorkgroup',
      }),
      icon: `<img src="${workgroupType.iconUrl}" />`,
      disabled: !isChangeable,
      action: () => {
        if (!row || !row.body) {
          return
        }
        const workgroupType = store.getState().project.wbsItemTypes.workgroup
        if (!row.editedData) {
          row.editedData = {}
        }
        row.editedData['body.wbsItem.wbsItemType'] =
          row.body.wbsItem.wbsItemType
        row.body = {
          ...row.body,
          wbsItem: {
            ...row.body.wbsItem,
            wbsItemType: workgroupType,
            baseWbsItemType: workgroupType,
          },
        }
        row.edited = true
        action.setData(moveRowsToLastChild(data, [row], undefined), [row])
      },
    },
  ]
}

export const addTicketMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  action: {
    setData: (d: ProjectPlanNewRow[], r: ProjectPlanNewRow[]) => void
    onAddTicket: (parentUuid: string, ticketType: WbsItemTypeVO) => void
    onAddTickets: (parentUuid: string) => void
  },
  generateCode: WbsItemCodeGenerator
): (DefaultMenuItem | MenuItemDef)[] => {
  const row = params.node?.data
  const ticketTypes = store.getState().project.ticketListTypes
  if (!row?.body || !ticketTypes) return []

  const ticketListSubMenuItems = addTicketListSubMenuItems(
    params,
    data,
    action.setData,
    generateCode
  )
  const ticketSubMenuItems = addTicketSubMenuItems(
    params,
    data,
    action.onAddTicket
  )
  const menus = [
    ticketListSubMenuItems.length
      ? {
          name: intl.formatMessage({
            id: 'bulksheet.contextMenu.ticketList',
          }),
          subMenu: ticketListSubMenuItems,
        }
      : undefined,
    ticketSubMenuItems.length
      ? {
          name: intl.formatMessage({
            id: 'bulksheet.contextMenu.ticket.add.multiple',
          }),
          action: () => action.onAddTickets(row.uuid),
        }
      : undefined,
    ticketSubMenuItems.length
      ? {
          name: intl.formatMessage({
            id: 'bulksheet.contextMenu.ticket',
          }),
          subMenu: ticketSubMenuItems,
        }
      : undefined,
  ].filter(v => !!v) as MenuItemDef[]
  if (menus.length) return ['separator', ...menus]
  return []
}

const addTicketListSubMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  setData: (d: ProjectPlanNewRow[], r: ProjectPlanNewRow[]) => void,
  generateCode: WbsItemCodeGenerator
): MenuItemDef[] => {
  const row = params.node?.data
  const ticketTypes = store.getState().project.ticketListTypes
  if (!row?.body || !ticketTypes) return []

  return ticketTypes
    .filter((ticketType: WbsItemTypeVO) =>
      ticketType.canBeChildOf(row.body!.wbsItem.wbsItemType)
    )
    .map(ticketType => ({
      name: ticketType.name,
      // /src/view/components/icons/TicketListSubMenuItemsIcon.tsx に配置したコンポーネントをhtmlに変換するために
      // ReactDOMServer.renderToStaticMarkupを使用している
      icon: ReactDOMServer.renderToStaticMarkup(
        TicketListSubMenuItemsIcon(ticketType)
      ),
      action: () => {
        const newTicketList = createNewProjectPlanNewRow(
          ticketType,
          generateCode
        )
        const newData = addRowsToLastChild(data, [newTicketList], row.uuid)
        const newRows = newData.filter(v => v.uuid === newTicketList.uuid)
        setData(newData, newRows)
        setTimeout(() => {
          params.node?.setExpanded(true)
          focusRow(params.api, newTicketList.uuid)
        }, 0)
      },
    }))
}

const addTicketSubMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  onAddTicket: (parentUuid: string, ticketType: WbsItemTypeVO) => void
): MenuItemDef[] => {
  const row = params.node?.data
  const ticketTypes = store.getState().project.ticketTypes
  if (!row?.body || !ticketTypes) return []

  return ticketTypes
    .filter((ticketType: WbsItemTypeVO) =>
      ticketType.canBeChildOf(row.body!.wbsItem.wbsItemType)
    )
    .map((ticketType: WbsItemTypeVO) => ({
      name: ticketType.name,
      icon: `<img src="${ticketType.iconUrl}" />`,
      action: () => onAddTicket(row.uuid, ticketType),
    }))
}

const getUniqueTargets = (
  selectedNodes: IRowNode<ProjectPlanNewRow>[]
): ProjectPlanNewRow[] => {
  const allChildren = selectedNodes
    .flatMap(v => allDescendants(v))
    .map(v => v?.data)
    .filter(v => v)
  return _.uniqBy(allChildren, 'uuid')
}

export const hasRowNodeAdded = (
  selectedNodes: IRowNode<ProjectPlanNewRow>[]
): boolean => {
  return selectedNodes.every(n => n.data?.added)
}

export const hasRowNodeActualHourRegistered = (
  selectedNodes: IRowNode<ProjectPlanNewRow>[]
): boolean => {
  const targets = getUniqueTargets(selectedNodes)
  return targets.some(target => {
    const wbsItem = target.body?.wbsItem
    const cumulation = target.body?.cumulation

    if (!wbsItem) return false
    if (wbsItem.wbsItemType?.isTask()) return !!wbsItem.actualHour
    if (cumulation) {
      return cumulation.sumActualHour && cumulation.sumActualHour !== 0
    }
    const hour = target.body?.wbsItem.actualHour
    return hour && hour !== 0
  })
}

export const hasRowNodeTicketListWithChild = (
  selectedNodes: IRowNode<ProjectPlanNewRow>[]
): boolean => {
  const targets = getUniqueTargets(selectedNodes)
  return targets.some(v => 0 < (v.body?.ticketCumulation?.countTicket ?? 0))
}

export const deleteRowMenuItems = (
  params: GetContextMenuItemsParams,
  selectedNodes: IRowNode<ProjectPlanNewRow>[],
  data: ProjectPlanNewRow[],
  action: () => void
): MenuItemDef[] => {
  const hasAddedNodes = hasRowNodeAdded(selectedNodes)
  const hasActualHourRegistered = hasRowNodeActualHourRegistered(selectedNodes)
  const hasTicketListWithChild = hasRowNodeTicketListWithChild(selectedNodes)
  const canDelete =
    hasAddedNodes || (!hasActualHourRegistered && !hasTicketListWithChild)
  return [
    {
      name: intl.formatMessage(
        { id: 'bulksheet.contextMenu.delete' },
        { count: selectedNodes.length }
      ),
      icon: getMenuIconHtml(ContextMenuItemId.REMOVE_ROW),
      action,
      disabled: !canDelete,
      tooltip: hasActualHourRegistered
        ? intl.formatMessage({
            id: 'bulksheet.contextMenu.remove.disabled.reason.actual.registerd',
          })
        : hasTicketListWithChild
          ? intl.formatMessage({
              id: 'bulksheet.contextMenu.remove.disabled.reason.exists.ticket',
            })
          : undefined,
      shortcut: intl.formatMessage({
        id: 'bulksheet.contextMenu.shortcut.row.delete',
      }),
    },
  ]
}

export const reportMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  action: { setData: (data: ProjectPlanNewRow[]) => void }
): (DefaultMenuItem | MenuItemDef)[] => {
  const row = params.node?.data
  const wbsItem = row?.body?.wbsItem
  const projectUuid = params.context.projectUuid
  const updateRow = (updated: ProjectPlanNewRow, node: IRowNode) => {
    updated.edited = true
    action.setData(updateRows(data, [updated]))
    node &&
      params.api.refreshCells({
        rowNodes: [node],
        columns: [
          'body.wbsItem.scheduledDate.startDate',
          'body.wbsItem.scheduledDate.endDate',
          'ganttChart',
        ],
      })
  }
  const updateAncestors = (
    before: ProjectPlanNewRow,
    after: ProjectPlanNewRow
  ) => {
    const ancestors = before.treeValue
      .slice(0, -1)
      .map(id => params.api.getRowNode(id)?.data)
      .filter(v => v) as ProjectPlanNewRow[]
    ReportReCalculator.update(before, after, ancestors).forEach(ancestor => {
      const node = params.api.getRowNode(ancestor.uuid)
      node?.setData(ancestor)
    })
  }
  const selectedNodes = getSelectedNode(params.api)
  const isSelectedMultipleRows = (selectedNodes?.length ?? 0) > 1
  if (!row || !wbsItem || !projectUuid) return []
  const menus = [
    // Open progress report
    params.node?.childrenAfterGroup &&
    params.node?.childrenAfterGroup.length > 0 &&
    !isSelectedMultipleRows
      ? {
          name: intl.formatMessage({ id: 'openProgressReport' }),
          icon: getMenuIconHtml(ContextMenuItemId.OPEN_PROGRESS_REPORT),
          action: () => {
            const searchCondition = {
              rootWbsItemUuid: wbsItem.uuid,
            }
            const viewConfig = dateTermToViewConfig(wbsItem.scheduledDate)
            openProgressReport(projectUuid, searchCondition, viewConfig)
          },
        }
      : undefined,
    // Open sprint report
    (wbsItem.wbsItemType.isProcess() || wbsItem.wbsItemType.isWorkgroup()) &&
    !row.added &&
    !isSelectedMultipleRows
      ? {
          name: intl.formatMessage({ id: 'openSprintReport' }),
          icon: getMenuIconHtml(ContextMenuItemId.OPEN_SPRINT_REPORT),
          action: async () => {
            const projectCode = store.getState().project.current?.code
            open(
              `/sprintReport/${projectCode}?rootWbsItem=${wbsItem.uuid}`,
              undefined,
              undefined,
              true
            )
          },
        }
      : undefined,
    // Open kanban
    row?.body?.cumulation?.countTask > 0 &&
    !row.added &&
    !isSelectedMultipleRows
      ? {
          name: intl.formatMessage({ id: 'kanban' }),
          icon: getMenuIconHtml(ContextMenuItemId.OPEN_KANBAN),
          action: () => {
            const projectCode = store.getState().project.current?.code
            const query: Partial<StatusKanbanSearchCondition> = {
              rootUuid: wbsItem.uuid,
            }
            openKanban(projectCode!, query)
          },
        }
      : undefined,
    // Search delayed wbs
    params.node?.childrenAfterGroup &&
    params.node?.childrenAfterGroup.length > 0 &&
    !isSelectedMultipleRows
      ? {
          name: intl.formatMessage({ id: 'searchDelayedWbs' }),
          icon: getMenuIconHtml(ContextMenuItemId.SEARCH_DELAYED_WBS),
          action: () => {
            openWbsItemSearch(
              projectUuid,
              SearchConditionEndDelayed.with({
                rootUuid: wbsItem.uuid,
              })
            )
          },
        }
      : undefined,
    // Open ticket list
    wbsItem.wbsItemType.isDeliverable() &&
    wbsItem.wbsItemType.isTicket() &&
    !isSelectedMultipleRows
      ? {
          name: intl.formatMessage({ id: 'openTicketList' }),
          icon: getMenuIconHtml(ContextMenuItemId.OPEN_TICKET_LIST),
          action: () => {
            openTicketList(wbsItem.uuid)
          },
        }
      : undefined,
    params.node?.childrenAfterGroup &&
    params.node?.childrenAfterGroup.length > 0
      ? {
          name: intl.formatMessage({ id: 'getPlanFromChildren' }),
          subMenu: [
            {
              name: intl.formatMessage({
                id: 'getPlanFromChildren.bothDate',
              }),
              action: async () => {
                try {
                  const selectedNodes = params.api.getSelectedNodes()
                  const uuids = selectedNodes
                    .filter(n => n.data?.body?.wbsItem && !n.data.added)
                    .map(n => n.data.uuid)
                  const dateRangeMap = await fetchScheduledDateRanges(uuids)
                  for (const node of selectedNodes) {
                    const descendants = allDescendants(node).map(v => v.data)
                    const row = node.data
                    if (!row.body?.wbsItem) continue
                    const earliestDate = resolveEarliestDate(
                      row,
                      descendants,
                      dateRangeMap.get(row.uuid)?.earliestDate
                    )
                    const latestDate = resolveLatestDate(
                      row,
                      descendants,
                      dateRangeMap.get(row.uuid)?.latestDate
                    )
                    const before = _.cloneDeep(row)
                    row.body.wbsItem.scheduledDate = {
                      startDate: earliestDate,
                      endDate: latestDate,
                    }
                    updateRow(row, node)
                    updateAncestors(before, row)
                  }
                } catch {
                  notifyFetchError(params)
                }
              },
              icon: getMenuIconHtml(
                ContextMenuItemId.GET_PLAN_FROM_CHILDREN_BOTH_DATE
              ),
            },
            {
              name: intl.formatMessage({
                id: 'getPlanFromChildren.earliestDate',
              }),
              action: async () => {
                try {
                  const selectedNodes = params.api.getSelectedNodes()
                  const uuids = selectedNodes
                    .filter(n => n.data?.body?.wbsItem && !n.data.added)
                    .map(n => n.data.uuid)
                  const dateRangeMap = await fetchScheduledDateRanges(uuids)
                  for (const node of selectedNodes) {
                    const descendants = allDescendants(node).map(v => v.data)
                    const row = node.data
                    if (!row.body?.wbsItem) continue
                    const before = _.cloneDeep(row)
                    row.body.wbsItem.scheduledDate.startDate =
                      resolveEarliestDate(
                        row,
                        descendants,
                        dateRangeMap.get(row.uuid)?.earliestDate
                      )
                    updateRow(row, node)
                    updateAncestors(before, row)
                  }
                } catch {
                  notifyFetchError(params)
                }
              },
              icon: getMenuIconHtml(
                ContextMenuItemId.GET_PLAN_FROM_CHILDREN_EARLIEST_DATE
              ),
            },
            {
              name: intl.formatMessage({
                id: 'getPlanFromChildren.latestDate',
              }),
              action: async () => {
                try {
                  const selectedNodes = params.api.getSelectedNodes()
                  const uuids = selectedNodes
                    .filter(n => n.data?.body?.wbsItem && !n.data.added)
                    .map(n => n.data.uuid)
                  const dateRangeMap = await fetchScheduledDateRanges(uuids)
                  for (const node of selectedNodes) {
                    const descendants = allDescendants(node).map(v => v.data)
                    const row = node.data
                    if (!row.body?.wbsItem) continue
                    const before = _.cloneDeep(row)
                    row.body.wbsItem.scheduledDate.endDate = resolveLatestDate(
                      row,
                      descendants,
                      dateRangeMap.get(row.uuid)?.latestDate
                    )
                    updateRow(row, node)
                    updateAncestors(before, row)
                  }
                } catch {
                  notifyFetchError(params)
                }
              },
              icon: getMenuIconHtml(
                ContextMenuItemId.GET_PLAN_FROM_CHILDREN_LATEST_DATE
              ),
            },
          ],
          disabled: !params.api
            .getSelectedNodes()
            .every(
              node =>
                node.childrenAfterGroup && node.childrenAfterGroup.length > 0
            ),
          icon: getMenuIconHtml(ContextMenuItemId.GET_PLAN_FROM_CHILDREN),
          tooltip: !(
            params.node.childrenAfterGroup &&
            params.node.childrenAfterGroup.length > 0
          )
            ? intl.formatMessage({
                id: 'bulksheet.contextMenu.disabled.not.exists.child.row',
              })
            : undefined,
        }
      : undefined,
  ].filter(v => !!v) as MenuItemDef[]
  if (menus.length) return ['separator', ...menus]
  return []
}

const notifyFetchError = (params: GetContextMenuItemsParams) => {
  const projectUuid = params.context?.projectUuid
  if (!projectUuid) return
  store.dispatch(
    addScreenMessage(projectUuid, {
      type: MessageLevel.WARN,
      title: intl.formatMessage({
        id: 'global.warning.systemError.server',
      }),
    })
  )
}

const resolveEarliestDate = (
  targetRow: IRowNode['data'],
  descendants: ProjectPlanNewRow[],
  dateFromCumulation?: DateVO
): string | undefined => {
  const displayed = calculateEarliestDateFromGrid(targetRow, descendants)
  const list = [dateFromCumulation, displayed].filter((v): v is DateVO => !!v)
  if (0 < list.length) {
    return DateVO.min(list)?.serialize()
  }
  const { projectUuid } = targetRow.body.wbsItem
  store.dispatch(
    addScreenMessage(projectUuid, {
      type: MessageLevel.WARN,
      title: intl.formatMessage({
        id: 'projectPlan.getPlanFromChildren.has.no.earliest.date',
      }),
    })
  )
  return undefined
}

const calculateEarliestDateFromGrid = (
  targetRow: IRowNode['data'],
  descendants: ProjectPlanNewRow[]
): DateVO | undefined => {
  const projectPlanUuid = targetRow.uuid
  const dateList = descendants
    .filter(
      v =>
        v.uuid !== projectPlanUuid &&
        v.treeValue.includes(projectPlanUuid) &&
        v.body?.wbsItem.status !== WbsItemStatus.DISCARD &&
        !!v.body?.wbsItem.scheduledDate?.startDate
    )
    .map(v => new DateVO(v.body!.wbsItem.scheduledDate!.startDate))
  return 0 < dateList.length ? DateVO.min(dateList) : undefined
}

const resolveLatestDate = (
  targetRow: IRowNode['data'],
  descendants: ProjectPlanNewRow[],
  dateFromCumulation?: DateVO
): string | undefined => {
  const displayed = calculateLatestDateFromGrid(targetRow, descendants)
  const list = [dateFromCumulation, displayed].filter((v): v is DateVO => !!v)
  if (0 < list.length) {
    return DateVO.max(list)?.serialize()
  }
  const { projectUuid } = targetRow.body.wbsItem
  store.dispatch(
    addScreenMessage(projectUuid, {
      type: MessageLevel.WARN,
      title: intl.formatMessage({
        id: 'projectPlan.getPlanFromChildren.has.no.latest.date',
      }),
    })
  )
  return undefined
}

const calculateLatestDateFromGrid = (
  targetRow: IRowNode['data'],
  descendants: ProjectPlanNewRow[]
): DateVO | undefined => {
  const projectPlanUuid = targetRow.uuid
  const dateList = descendants
    .filter(
      v =>
        v.uuid !== projectPlanUuid &&
        v.treeValue.includes(projectPlanUuid) &&
        v.body?.wbsItem.status !== WbsItemStatus.DISCARD &&
        !!v.body?.wbsItem.scheduledDate?.endDate
    )
    .map(v => new DateVO(v.body!.wbsItem.scheduledDate!.endDate))
  return 0 < dateList.length ? DateVO.max(dateList) : undefined
}

type ScheduledDateRange = {
  earliestDate?: DateVO
  latestDate?: DateVO
}

const fetchScheduledDateRanges = async (
  uuids: string[]
): Promise<Map<string, ScheduledDateRange>> => {
  if (uuids.length === 0) return new Map()
  const ranges = (await getScheduledDateRanges(uuids)).json
  const map = new Map<string, ScheduledDateRange>()
  for (const range of ranges) {
    map.set(range.uuid, {
      earliestDate: toDateVO(range.earliestScheduledDate),
      latestDate: toDateVO(range.latestScheduledDate),
    })
  }
  return map
}

const toDateVO = (date?: string): DateVO | undefined =>
  date && canParseDate(date) ? new DateVO(date) : undefined

export const switchRootWbsMenuItems = (
  params: GetContextMenuItemsParams
): MenuItemDef[] => {
  const row: ProjectPlanNewRow = params.node?.data

  return [
    {
      name: intl.formatMessage({
        id: 'bulksheet.contextMenu.switchRoot.wbsItem',
      }),
      disabled: !row,
      action: async () => {
        window.open(
          `${window.location.origin}/projectPlan/${
            store.getState().project.current?.code
          }?treeRootUuid=${row.uuid}`
        )
      },
      icon: getMenuIconHtml(ContextMenuItemId.SWITCH_ROOT_WBS_ITEM),
    },
  ]
}

export const duplicateSameNameTaskMenuItems = (
  params: GetContextMenuItemsParams,
  data: ProjectPlanNewRow[],
  setData: (d: ProjectPlanNewRow[], r: ProjectPlanNewRow[]) => void,
  generateCode: WbsItemCodeGenerator
): MenuItemDef[] => {
  const selectedNodes = params.api.getSelectedNodes()
  const targetNodes =
    selectedNodes.length > 0 ? selectedNodes : params.node ? [params.node] : []

  if (targetNodes.length === 0) return []

  const types = store.getState().project.wbsItemTypes

  // 全ての選択行が子にタスクを追加できるかチェック
  const validNodes = targetNodes.filter(node => {
    const row = node.data as ProjectPlanNewRow | undefined
    if (!row?.body?.wbsItem) return false
    return types.task.canBeChildOf(row.body.wbsItem.wbsItemType)
  })

  if (validNodes.length === 0) return []

  // 全ての選択行にdisplayNameがあるかチェック
  const allHaveDisplayName = validNodes.every(
    node => !!node.data?.body?.wbsItem?.displayName
  )

  return [
    {
      name: intl.formatMessage({
        id: 'bulksheet.contextMenu.duplicate.sameNameTask',
      }),
      icon: `<img src="${types.task.iconUrl}" />`,
      disabled: !allHaveDisplayName,
      action: () => {
        let currentData = data
        const allNewRows: ProjectPlanNewRow[] = []

        validNodes.forEach(node => {
          const row = node.data as ProjectPlanNewRow
          const newRow = createSameNameTaskRow(
            row.body!.wbsItem,
            types.task,
            generateCode
          )
          currentData = addRowsToLastChild(currentData, [newRow], row.uuid)
          allNewRows.push(newRow)
        })

        const newRowUuids = allNewRows.map(r => r.uuid)
        const newRows = currentData.filter(v => newRowUuids.includes(v.uuid))
        setData(currentData, newRows)

        setTimeout(() => {
          validNodes.forEach(node => {
            node.setExpanded(true)
          })
          if (allNewRows.length > 0) {
            focusRow(params.api, allNewRows[0].uuid)
          }
        }, 0)
      },
      tooltip: !allHaveDisplayName
        ? intl.formatMessage({
            id: 'bulksheet.contextMenu.duplicate.sameNameTask.disabled.noName',
          })
        : undefined,
    },
  ]
}
